/**
 * Manages interactive elements in the header, such as dropdown menus and the search panel.
 * This script handles showing/hiding panels on click, manages ARIA states for accessibility,
 * controls keyboard navigation (Enter, Space, Escape), and handles focus management
 * for an accessible user experience.
 */
export function initializeInteractiveHeader() {
    const header = document.getElementById('header');
    if (!header) return;

    const htmlEl = document.documentElement;
    const mobileMenuToggle = header.querySelector('.header__menu-toggle');
    const mobileNavContainer = header.querySelector('.header__navigation');

    /**
     * Closes the mobile menu and updates ARIA attributes.
     */
    const closeMobileMenu = () => {
        htmlEl.classList.remove('menu-open');
        if (mobileMenuToggle) {
            mobileMenuToggle.setAttribute('aria-expanded', 'false');
        }
    };

    if (mobileMenuToggle && mobileNavContainer) {
        mobileMenuToggle.addEventListener('click', () => {
            htmlEl.classList.toggle('menu-open');
            const isExpanded = htmlEl.classList.contains('menu-open');
            mobileMenuToggle.setAttribute('aria-expanded', String(isExpanded));
        });

        // Close menu when a link inside is clicked
        mobileNavContainer.addEventListener('click', (event) => {
            if (event.target.closest('a')) {
                closeMobileMenu();
            }
        });
    }

    const nav = header.querySelector('.header__navigation');
    const searchToggle = header.querySelector('.header__search-toggle');
    const searchContainer = header.querySelector('.header__search-container');
    const searchForm = header.querySelector('#header-search-form');
    const searchInput = header.querySelector('#header-search-input');
    const menuPanel = header.querySelector('#header-menu-panel');

    if (!menuPanel) {
        console.warn('Header menu panel not found. Desktop sub-menu backgrounds will not work.');
    }

    const allPanels = Array.from(header.querySelectorAll('.sub-menu, .header__search-form'));
    let activeTrigger = null; // To store the element that opened a panel for focus return

    /**
     * Hides all panels, resets ARIA states, and returns focus to the trigger.
     */
    const hideAllPanels = () => {
        allPanels.forEach(panel => {
            panel.classList.remove('is-visible');
            panel.setAttribute('aria-hidden', 'true');
        });

        if (activeTrigger) {
            activeTrigger.setAttribute('aria-expanded', 'false');
            // Return focus to the element that opened the panel
            activeTrigger.focus();
            activeTrigger = null;
        }

        menuPanel.style.height = '0px';
        header.classList.remove('is-panel-active');
    };

    // The following logic is for desktop dropdowns and search, which depends on `menuPanel`.
    if (menuPanel) {
        // Set initial ARIA states for accessibility
        allPanels.forEach(panel => panel.setAttribute('aria-hidden', 'true'));
        if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');

        /**
         * Shows a specific panel, sets ARIA states, and manages focus.
         * @param {HTMLElement} panel The panel element to show.
         * @param {HTMLElement} trigger The element that triggered the panel to open.
         */
        const showPanel = (panel, trigger) => {
            // Hide any other visible panel first
            const currentlyVisible = allPanels.find(p => p.classList.contains('is-visible') && p !== panel);
            if (currentlyVisible) {
                currentlyVisible.classList.remove('is-visible');
                currentlyVisible.setAttribute('aria-hidden', 'true');
                if (activeTrigger) {
                    activeTrigger.setAttribute('aria-expanded', 'false');
                }
            }

            panel.classList.add('is-visible');
            panel.setAttribute('aria-hidden', 'false');

            // Update the trigger
            activeTrigger = trigger;
            if (activeTrigger) {
                activeTrigger.setAttribute('aria-expanded', 'true');
            }

            // Calculate and set the background panel height using scrollHeight
            const panelHeight = panel.scrollHeight;
            menuPanel.style.height = `${panelHeight}px`;
            header.classList.add('is-panel-active');

            // --- Focus Management ---
            // Use a timeout to ensure the panel is visible and focusable before moving focus.
            setTimeout(() => {
                if (panel === searchForm && searchInput) {
                    searchInput.focus();
                } else {
                    const firstFocusable = panel.querySelector('a, button');
                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                }
            }, 100); // A small delay can help, especially with transitions.
        };

        // --- Sub-menu click & keydown logic ---
        if (nav) {
            const menuItemsWithChildren = Array.from(nav.querySelectorAll('.menu-item-has-children'));

            menuItemsWithChildren.forEach(item => {
                const link = item.querySelector('a[aria-haspopup="true"]');
                if (!link) return;

                const subMenuId = link.getAttribute('aria-controls');
                const subMenu = document.getElementById(subMenuId);
                if (!subMenu) return;

                const toggleSubMenu = (event) => {
                    // This is the crucial part to stop the browser's default navigation
                    event.preventDefault();

                    const isVisible = subMenu.classList.contains('is-visible');

                    if (isVisible) {
                        hideAllPanels();
                    } else {
                        showPanel(subMenu, link);
                    }
                };

                link.addEventListener('click', toggleSubMenu);

                link.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter' || event.key === ' ') { // ' ' is for Space key
                        toggleSubMenu(event);
                    }
                });
            });
        }

        // --- Search toggle click logic ---
        if (searchToggle && searchForm) {
            searchToggle.addEventListener('click', () => {
                const isVisible = searchForm.classList.contains('is-visible');
                if (isVisible) {
                    hideAllPanels();
                } else {
                    showPanel(searchForm, searchToggle);
                }
            });
        }
    }


    // --- Global event listeners to close panels ---
    document.addEventListener('click', (event) => {
        // If no panel is open, there's nothing to do.
        const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
        if (!isAnyPanelVisible) { // This listener only handles desktop panels on outside click
            return;
        }

        // Hide panels if the click is outside the main interactive areas:
        // the navigation and the search container.
        if (
            (!nav || !nav.contains(event.target)) &&
            (!searchContainer || !searchContainer.contains(event.target))
        ) {
            hideAllPanels();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            const isMobileMenuOpen = htmlEl.classList.contains('menu-open');
            const isAnyPanelVisible = allPanels.some(p => p.classList.contains('is-visible'));
            if (isAnyPanelVisible || isMobileMenuOpen) {
                event.preventDefault(); // Prevent other escape actions if a panel is open
                closeEverything();
            }
        }
    });

    /** Closes all interactive header elements (mobile menu and desktop panels). */
    const closeEverything = () => {
        hideAllPanels();
        closeMobileMenu();
    };

    // Listen for the custom event dispatched by Barba.js to close panels before a page transition.
    document.addEventListener('barba-leave-start', closeEverything);

    // Hide panels on resize or orientation change, as the layout may have changed.
    window.addEventListener('resize', closeEverything);
    window.addEventListener('orientationchange', closeEverything);
}